/*
 * manual-payment-reward-simple.js
 * - Only 2 tiers:
 *   - > 0  => 1 gift
 *   - >= 1499 => 2 gifts
 * - Max 2 gifts total
 * - Always remove old gifts before new selection
 * - Uses concurrent add.js calls for speed
 */

(function () {
  if (!window.location.pathname.includes('/cart')) return;

  // --- Config ---
  const HANDLES = [
    'vom-m-volume-free-gift',
    'elasty-f-1-syringe-free-gift',
    'greentox-free-gift',
    'vom-o-intensive-free-gift',
    'vom-v-light-free-gift'
  ];

  // Shopify product IDs for all free gift products — must match disabled_atc_ids in product-form.liquid
  const GIFT_PRODUCT_IDS = [
    9014226288897, 9323896144129, 9323894898945, 9323894702337, 9352668086529
  ];

  const POPUP_ID = 'manual-payment-reward-popup';
  const PRODUCT_LIST_ID = 'manual-payment-product-list';
  const MESSAGE_ID = 'manual-payment-message';
  const ADD_BTN_ID = 'manual-payment-add-button';
  const ADD_MORE_BTN_ID = 'manual-payment-add-more';
  const CANCEL_BTN_ID = 'manual-payment-cancel';
  const TIER_INFO_ID = 'manual-payment-tier-info';
  const SELECTION_INFO_ID = 'manual-payment-selection-info';

  const ADD_MORE_URL = '/collections/hot-deals-collection';
  const LOADING_ID = 'manual-payment-loading';

  // --- DOM refs ---
  const popup = document.getElementById(POPUP_ID);
  if (!popup) return;

  const listEl = document.getElementById(PRODUCT_LIST_ID);
  const messageEl = document.getElementById(MESSAGE_ID);
  const addButton = document.getElementById(ADD_BTN_ID);
  const addMoreBtn = document.getElementById(ADD_MORE_BTN_ID);
  const cancelBtn = document.getElementById(CANCEL_BTN_ID);
  const closeBtn = popup.querySelector('.manual-payment-popup__close');
  const tierInfoEl = document.getElementById(TIER_INFO_ID);
  const selectionInfoEl = document.getElementById(SELECTION_INFO_ID);
  const loadingEl = document.getElementById(LOADING_ID);
  const loadingTextEl = loadingEl ? loadingEl.querySelector('.manual-payment-popup__loading-text') : null;

  // --- State ---
  let productData = [];
  let selectedVariantIds = new Set();
  let maxSelectable = 0;
  let isBusy = false;
  let currencySymbol = (window.Shopify && Shopify.currency && Shopify.currency.active) ? Shopify.currency.active : '';
  let confettiReadyPromise = null;

  function ensureConfettiLoaded() {
    if (window.confetti) return Promise.resolve();
    if (confettiReadyPromise) return confettiReadyPromise;
    confettiReadyPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.6.0/dist/confetti.browser.min.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = reject;
      document.head.appendChild(script);
      setTimeout(() => reject(new Error('confetti load timeout')), 7000);
    }).catch(() => {
      // swallow errors; we just skip confetti if it fails to load
    });
    return confettiReadyPromise;
  }

  function setLoading(isLoading, text) {
    isBusy = isLoading;
    if (!loadingEl) return;
    if (text && loadingTextEl) loadingTextEl.textContent = text;
    loadingEl.hidden = !isLoading;
    if (isLoading) {
      popup.classList.add('is-loading');
    } else {
      popup.classList.remove('is-loading');
    }
  }

  // --- Cart helpers ---
  async function getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  async function changeCartItem(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    }).then(r => r.json());
  }

  async function addVariantToCart(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { _manual_payment_reward: true }
      })
    }).then(r => r.json());
  }

  function subtotalUnitsFromCart(cart) {
    // Sum only paid (non-gift) items so gift product prices don't inflate the tier calculation.
    // item.line_price is Shopify's line total in cents (unit_price × quantity).
    const paidCents = cart.items
      .filter(item => !isGiftProduct(item))
      .reduce((sum, item) => sum + item.line_price, 0);
    return Math.round(paidCents / 100);
  }

  // Only 2 tiers: >0 = 1 gift, >=1499 = 2 gifts
  function giftsAllowedForSubtotal(subtotalUnits) {
    if (subtotalUnits >= 1499) return 2;
    if (subtotalUnits > 0) return 1;
    return 0;
  }

  // Tagged items added by the popup flow
  function isRewardLine(item) {
    return !!(item.properties && item.properties._manual_payment_reward);
  }
  // Catches ALL gift items — tagged OR added directly from product/search pages
  function isGiftProduct(item) {
    return isRewardLine(item) || GIFT_PRODUCT_IDS.includes(item.product_id);
  }

  async function removeAllRewardLines() {
    const cart = await getCart();
    // Remove ALL gift items — both popup-tagged and any added directly via search/product pages
    const giftLines = cart.items.filter(isGiftProduct);
    if (!giftLines.length) return;
    await Promise.all(giftLines.map(item => changeCartItem(item.key, 0)));
  }

  async function ensureNoFreeGiftsWhenNoPaidItems() {
    const cart = await getCart();
    const paidItems = cart.items.filter(item => !isGiftProduct(item));
    const giftLines = cart.items.filter(isGiftProduct);
    if (!paidItems.length && giftLines.length) {
      await Promise.all(giftLines.map(item => changeCartItem(item.key, 0)));
    }
  }

  async function hasPaidItems() {
    const cart = await getCart();
    return cart.items.some(item => !isGiftProduct(item));
  }

  function showCartError(message) {
    const errEl = document.getElementById('cart-gift-error-message');
    if (!errEl) return;
    errEl.textContent = message;
    errEl.hidden = false;
    errEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => { errEl.hidden = true; errEl.textContent = ''; }, 5000);
  }

  async function updateTierDisplay() {
    const cart = await getCart();
    const subtotal = subtotalUnitsFromCart(cart);
    const allowed = giftsAllowedForSubtotal(subtotal);
    maxSelectable = allowed;

    if (tierInfoEl) {
      if (allowed === 0) {
        tierInfoEl.textContent = 'Add products to get a free gift.';
      } else if (allowed === 1) {
        tierInfoEl.textContent = 'You qualify for 1 free gift.';
      } else {
        tierInfoEl.textContent = 'You qualify for 2 free gifts.';
      }
    }
    if (selectionInfoEl) {
      selectionInfoEl.textContent = `Selected ${selectedVariantIds.size}. You can select up to ${maxSelectable}.`;
    }
    if (addButton) {
      addButton.disabled = allowed === 0;
    }
  }

  function buildProducts() {
    if (!listEl) return;
    listEl.innerHTML = '';
    productData.forEach((product, index) => {
      const variant = product.variants && product.variants[0];
      if (!variant) return;

      const li = document.createElement('li');
      li.className = 'splide__slide';
      li.innerHTML = `
        <div class="manual-payment-popup__product" data-variant-id="${variant.id}">
          <div class="manual-payment-popup__product-image">
            <img src="${product.images && product.images[0] ? product.images[0] : ''}" alt="${product.title}">
          </div>
          <div class="manual-payment-popup__product-info">
            <h3 class="manual-payment-popup__product-title">${product.title}</h3>
            <div class="manual-payment-popup__price">
              <span class="current-price">${(product.price/100).toFixed(2)} ${currencySymbol}</span>
            </div>
          </div>
          <div class="manual-payment-popup__product-checkbox">
            <input class="mr-checkbox" type="checkbox" id="manual-product-${index}" value="${variant.id}">
          </div>
        </div>`;
      listEl.appendChild(li);
    });

    try {
      const splideRoot = popup.querySelector('.splide');
      if (splideRoot && typeof Splide === 'function') {
        if (splideRoot._splide && typeof splideRoot._splide.destroy === 'function') {
          splideRoot._splide.destroy();
        }
        const spl = new Splide(splideRoot, {
          perPage: 4,
          gap: '1rem',
          pagination: true,
          arrows: false,
          breakpoints: { 768: { perPage: 3 } }
        });
        spl.mount();
      }
    } catch (e) {}

    listEl.querySelectorAll('.manual-payment-popup__product').forEach(prod => {
      const cb = prod.querySelector('input[type="checkbox"]');
      if (!cb) return;

      cb.addEventListener('change', function () {
        if (this.checked) {
          if (selectedVariantIds.size >= maxSelectable) {
            this.checked = false;
            if (messageEl) {
              messageEl.textContent = `You can only select up to ${maxSelectable} gift(s).`;
              setTimeout(() => { messageEl.textContent = ''; }, 2000);
            }
            return;
          }
          selectedVariantIds.add(this.value);
          prod.classList.add('selected');
        } else {
          selectedVariantIds.delete(this.value);
          prod.classList.remove('selected');
        }
        if (selectionInfoEl) {
          selectionInfoEl.textContent = `Selected ${selectedVariantIds.size}. You can select up to ${maxSelectable}.`;
        }
      });

      prod.style.cursor = 'pointer';
      prod.addEventListener('click', function (e) {
        if (e.target.closest('input[type="checkbox"]')) return;
        const checkbox = this.querySelector('input[type="checkbox"]');
        if (!checkbox) return;
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  // --- Popup ---
  async function openPopup() {
    if (isBusy) return;
    setLoading(true, 'Checking your free gift eligibility...');
    try {
      // Guard: cart must contain at least one paid (non-gift) item
      const cart = await getCart();
      const paidItems = cart.items.filter(item => !isGiftProduct(item));
      if (!paidItems.length) {
        setLoading(false);
        showCartError('Please add products to your cart before proceeding to checkout.');
        return;
      }

      // Pre-populate selectedVariantIds from gifts already in cart so existing
      // selections are visible and preserved when the popup opens.
      // Actual removal + re-add happens only when the user confirms in addButton.
      selectedVariantIds.clear();
      cart.items.filter(isGiftProduct).forEach(item => {
        selectedVariantIds.add(String(item.variant_id));
      });

      popup.classList.remove('hidden');
      popup.classList.add('show');
      document.body.classList.add('manual-payment-popup-open');

      // Calculate allowed gifts BEFORE reflecting selections, so maxSelectable is set
      await updateTierDisplay();

      // Reflect pre-existing gift selections on the checkboxes
      if (listEl) {
        listEl.querySelectorAll('.manual-payment-popup__product').forEach(prod => {
          const cb = prod.querySelector('input[type="checkbox"]');
          if (!cb) return;
          const isSelected = selectedVariantIds.has(cb.value);
          cb.checked = isSelected;
          prod.classList.toggle('selected', isSelected);
        });
      }

      // If pre-selected gifts exceed the current tier (e.g. cart was reduced),
      // deselect the excess so the counter stays accurate
      if (selectedVariantIds.size > maxSelectable) {
        const excess = Array.from(selectedVariantIds).slice(maxSelectable);
        excess.forEach(id => {
          selectedVariantIds.delete(id);
          if (listEl) {
            const prod = listEl.querySelector(`[data-variant-id="${id}"]`);
            if (prod) {
              const cb = prod.querySelector('input[type="checkbox"]');
              if (cb) cb.checked = false;
              prod.classList.remove('selected');
            }
          }
        });
      }

      // Sync selection counter after adjustments
      if (selectionInfoEl) {
        selectionInfoEl.textContent = `Selected ${selectedVariantIds.size}. You can select up to ${maxSelectable}.`;
      }
    } finally {
      setLoading(false);
    }
  }

  function closePopup() {
    popup.classList.add('hidden');
    popup.classList.remove('show');
    document.body.classList.remove('manual-payment-popup-open');
  }

  if (closeBtn) closeBtn.addEventListener('click', closePopup);
  if (addMoreBtn) addMoreBtn.addEventListener('click', () => { window.location.href = ADD_MORE_URL; });
  if (cancelBtn) cancelBtn.addEventListener('click', async function () {
    closePopup();
    // Guard: must have paid items before allowing checkout without gifts
    const cartNow = await getCart();
    const paidNow = cartNow.items.filter(item => !isGiftProduct(item));
    if (!paidNow.length) {
      showCartError('Please add products to your cart before checking out.');
      return;
    }
    window.location.href = '/checkout';
  });

  // --- Checkout interception (simple) ---
  function attachCheckoutInterceptors() {
    document.addEventListener('click', function (e) {
      const target = e.target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]');
      if (!target) return;
      if (!window.location.pathname.includes('/cart')) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      openPopup();
    }, true);

    const cartForm = document.querySelector('form[action*="/cart"]');
    if (cartForm) {
      cartForm.addEventListener('submit', function (e) {
        try {
          if (e.submitter && e.submitter.name === 'checkout') {
            e.preventDefault();
            e.stopImmediatePropagation();
            openPopup();
          }
        } catch (err) {}
      }, true);
    }
  }

  // --- Add gifts then checkout ---
  if (addButton) {
    addButton.addEventListener('click', async function () {
      if (!selectedVariantIds.size) {
        if (messageEl) {
          messageEl.textContent = 'Please select at least one gift.';
          setTimeout(() => { messageEl.textContent = ''; }, 2000);
        }
        return;
      }
      if (selectedVariantIds.size > maxSelectable) {
        if (messageEl) {
          messageEl.textContent = `You can only select up to ${maxSelectable} gift(s).`;
          setTimeout(() => { messageEl.textContent = ''; }, 2000);
        }
        return;
      }

      addButton.disabled = true;
      setLoading(true, 'Adding your free gifts…');
      try {
        await removeAllRewardLines();

        // Add gifts sequentially — Shopify's cart API does not safely handle
        // concurrent POST /cart/add.js calls; parallel writes race against the
        // same cart state and one silently overwrites the other.
        for (const id of Array.from(selectedVariantIds)) {
          await addVariantToCart(id);
        }

        await ensureConfettiLoaded();
        if (window.confetti) {
          try {
            window.confetti({
              zIndex: 2147483000,
              particleCount: 180,
              spread: 80,
              origin: { y: 0.35 }
            });
          } catch (e) {}
        }

        if (messageEl) {
          messageEl.innerHTML =
            '<div class="free-offer-accepted">Your free product(s) have been added! Redirecting to checkout…</div>';
        }

        setTimeout(() => { window.location.href = '/checkout'; }, 900);
      } catch (e) {
        console.error('[ManualReward] add error', e);
        if (messageEl) {
          messageEl.textContent = 'Something went wrong. Please try again.';
        }
        addButton.disabled = false;
      } finally {
        setLoading(false);
      }
    });
  }

  // --- Init products + minimal cleanup ---
  async function fetchProduct(handle) {
    return fetch(`/products/${handle}.js`).then(r => r.json()).catch(() => null);
  }

  async function init() {
    setLoading(true, 'Loading free gifts…');
    try {
      ensureConfettiLoaded();
      const results = await Promise.all(HANDLES.map(h => fetchProduct(h)));
      productData = results.filter(Boolean);
      buildProducts();
    } catch (e) {
      console.error('[ManualReward] product load', e);
    }

    // Only minimal cleanup (fast)
    await ensureNoFreeGiftsWhenNoPaidItems();
    attachCheckoutInterceptors();
    await updateTierDisplay();
    setLoading(false);
  }

  init();
})();
